import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import MaterialTable, { Options, Query } from '@material-table/core';
import { Equipment } from '../models/Equipment';
import { getApiEquipmentList, deleteApiEquipmentEquipmentid } from '../services/equipment';
import { urls, route } from '../route';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { confirmAndDelete } from '../utils';
import { useSearchParams } from 'react-router-dom';
export const EquipmentList = () => {
    const history = useNavigate();
    const ref = useRef<any>();
    const [searchParams, setSearchParams] = useSearchParams()

    const urlPage = Math.max(0, Number(searchParams.get("page") ?? 0) || 0)
    const urlPageSize = Math.max(1, Number(searchParams.get("pageSize") ?? 10) || 10)
    const urlSearch = searchParams.get("search") ?? ""
    const urlSortBy = (searchParams.get("sortBy") ?? "").trim() || undefined
    const urlSortOrder =
        searchParams.get("sortOrder") === "asc"
            ? "asc"
            : searchParams.get("sortOrder") === "desc"
                ? "desc"
                : undefined

    const setUrlParams = useCallback((next: {
        page?: number
        pageSize?: number
        search?: string
        sortBy?: string
        sortOrder?: "asc" | "desc"
    }) => {
        const current = searchParams.toString()
        const sp = new URLSearchParams(searchParams)
        if (typeof next.page === "number") sp.set("page", String(Math.max(0, next.page)))
        if (typeof next.pageSize === "number") sp.set("pageSize", String(Math.max(1, next.pageSize)))
        if (typeof next.search === "string") {
            const v = next.search.trim()
            if (v) sp.set("search", v)
            else sp.delete("search")
        }
        if (typeof next.sortBy === "string") {
            const v = next.sortBy.trim()
            if (v) sp.set("sortBy", v)
            else sp.delete("sortBy")
        }
        if (next.sortOrder === "asc" || next.sortOrder === "desc") sp.set("sortOrder", next.sortOrder)
        else if (next.sortOrder === undefined) sp.delete("sortOrder")
        const nextStr = sp.toString()
        if (nextStr !== current) setSearchParams(sp, { replace: true })
    }, [searchParams, setSearchParams])

    const options = useMemo<Options<Equipment>>(
        () => ({
            actionsColumnIndex: -1,
            selection: true,
            search: true,
            maxColumnSort: 1,
            pageSize: urlPageSize,
            initialPage: urlPage,
            searchText: urlSearch,
        }),
        [urlPage, urlPageSize, urlSearch]
    )

    const dataLoader = useCallback(async (q: Query<Equipment>) => {
        const pageIndex = q.page ?? 0
        const pageSize = q.pageSize ?? 10
        const skip = pageIndex * pageSize

        const rawSortBy = q.orderBy?.field
        const sortBy = Array.isArray(rawSortBy) ? rawSortBy[0] : rawSortBy
        const sortOrder = q.orderDirection

        const search = (q.search ?? "").trim()
        setUrlParams({
            page: pageIndex,
            pageSize,
            search,
            sortBy: typeof sortBy === "string" ? sortBy : "",
            sortOrder,
        })
        const res = await getApiEquipmentList({ skip, limit: pageSize, search, sortBy, sortOrder })

        const totalCount =
            typeof res.total === "number"
                ? res.total
                : pageIndex * pageSize + res.items.length
        const allowedMaxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
        const safePage = Math.min(pageIndex, allowedMaxPage)

        return { data: res.items, page: safePage, totalCount }
    }, [])
    return <div>
        <MaterialTable<Equipment>
            title="Equipment"
            options={options}
            tableRef={ref}
            columns={[
                { title: "Id", field: "id", cellStyle: { width: "5%" } },
                { title: "Name", field: "name", defaultSort: urlSortBy === "name" ? urlSortOrder : undefined, width: "90%" },
                {
                    title: 'Tools',
                    render: (e) => (<>
                        <IconButton onClick={() => history(`/app/equipment/edit/${e.id}`)} style={{ float: "right" }}><Icon>edit</Icon></IconButton>
                        <IconButton onClick={confirmAndDelete(e, deleteApiEquipmentEquipmentid, ref)} style={{ float: "right" }}><Icon>delete</Icon></IconButton>
                    </>)
                }
            ]}
            data={dataLoader}
            actions={[{
                icon: "delete",
                tooltip: "Delete",
                onClick: async (event, data) => {
                    const confirmation = window.confirm("Are you sure you want to delete the selected items?")

                    if (confirmation && Array.isArray(data)) {
                        for (const i of data) {
                            await deleteApiEquipmentEquipmentid(i.id);
                        }
                        if (ref.current) {
                            ref.current.onQueryChange();
                        }
                    }
                }
            }, {
                icon: "add",
                tooltip: "Add New",
                isFreeAction: true,
                onClick: () => history(route(urls.equipmentAdd)),
            }]}
        />

    </div>
};